import { useFormikContext } from 'formik';
import { ReactElement, useEffect, useState } from 'react';
import { useAppSelector } from '../../../redux/hooks';
import {
  selectLEAManagementDataEntryMode,
  selectLEAManagementFormViewModel,
} from '../../../redux/slices/leaManagementSlice';
import { DataEntryMode } from '../../../types/DataEntryMode';
import { LEAManagementFormViewModel } from '../../../types/LEAManagementFormViewModel';
import { LEAManagementModel } from '../../../types/LEAManagementModel';
import { PanelType } from '../../../types/PanelType';
import { FormDropdownChangeEventConfig } from '../../../types/propTypes/FormDropdownPropTypes';
import { FormTextboxChangeEventConfig } from '../../../types/propTypes/FormTextboxPropTypes';
import { LEAManagementValidationEditSchema } from '../../../types/Validation/LEAManagementValidationEditSchema';
import { LEAManagementValidationSchema } from '../../../types/Validation/LEAManagementValidationSchema';
import {
  getCountyAnsiCodeFromOptions,
  isStateAdmin,
} from '../../../utilities/userUtilities';
import FormDropdown from '../../FormInputs/FormDropdown/FormDropdown';
import FormTextbox from '../../FormInputs/FormTextbox/FormTextbox';
import Panel from '../../Panel/Panel';

const LEAManagementPhysicalAddressPanel = (): ReactElement => {
  const { values, setFieldValue } = useFormikContext<LEAManagementModel>();
  const formViewModel: LEAManagementFormViewModel = useAppSelector(
    selectLEAManagementFormViewModel
  );

  const [isShowPhysicalCounty, setIsShowPhysicalCounty] = useState(false);
  const DEFAULT_STATE = 'Mississippi';

  const [
    arePhysicalAddressFieldsRequired,
    setArePhysicalAddressFieldsRequired,
  ] = useState(false);

  const getRequiredPhysicalAddressLabel = (): string => {
    return arePhysicalAddressFieldsRequired ? 'forced-required-label' : '';
  };

  const dataEntryMode: DataEntryMode = useAppSelector(
    selectLEAManagementDataEntryMode
  );

  const updatePhysicalAddressRequiredFlag = async (
    config: FormTextboxChangeEventConfig
  ): Promise<void> => {
    await setFieldValue(config.field, config.value);
    const updatedValues = {
      ...values,
      [config.field]: config.value,
    };
    const isRequired =
      config.value === '' &&
      updatedValues.physicalAddressStreetNumberAndName === '' &&
      updatedValues.physicalCity === '' &&
      updatedValues.physicalState === '' &&
      updatedValues.physicalZipCode === ''
        ? false
        : true;
    setArePhysicalAddressFieldsRequired(isRequired);
  };

  const updatePhysicalCountyAnsiCode = async (
    config: FormDropdownChangeEventConfig
  ): Promise<void> => {
    const physicalCountyAnsiCode = getCountyAnsiCodeFromOptions(
      formViewModel.countyAnsiCodeOptions,
      config
    );
    /* istanbul ignore next */
    if (config.setValueHook && physicalCountyAnsiCode) {
      config.setValueHook('physicalCountyAnsiCode', physicalCountyAnsiCode);
    }
  };

  const updateShowPhysicalCounty = async (
    config: FormDropdownChangeEventConfig
  ): Promise<void> => {
    const showCounty = config.value === DEFAULT_STATE ? true : false;
    setIsShowPhysicalCounty(showCounty);
    if (!showCounty) {
      setFieldValue('physicalCounty', '');
      setFieldValue('physicalCountyAnsiCode', '');
    }

    if (config.value === undefined) {
      setArePhysicalAddressFieldsRequired(false);
    } else {
      setArePhysicalAddressFieldsRequired(true);
    }
  };

  useEffect(() => {
    const isRequired =
      values.physicalAddressStreetNumberAndName === '' &&
      values.physicalCity === '' &&
      (values.physicalState === '' || values.physicalState === undefined) &&
      values.physicalZipCode === ''
        ? false
        : true;

    setArePhysicalAddressFieldsRequired(isRequired);
    const showCounty = values.physicalState === DEFAULT_STATE ? true : false;
    setIsShowPhysicalCounty(showCounty);

    if (!showCounty) {
      setFieldValue('physicalCounty', '');
      setFieldValue('physicalCountyAnsiCode', '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values]);

  return (
    <Panel panelType={PanelType.INFO} heading="Physical Address">
      <div className="field-row">
        <FormTextbox
          displayName="Address Street Number and Name"
          field="physicalAddressStreetNumberAndName"
          classConfig={{ labelClasses: getRequiredPhysicalAddressLabel() }}
          validationSchema={
            dataEntryMode === 'new'
              ? LEAManagementValidationSchema
              : LEAManagementValidationEditSchema
          }
          onChangeEvent={updatePhysicalAddressRequiredFlag}
          disabled={!isStateAdmin()}
        />
        <FormTextbox
          displayName="Apt., Suite, Unit, Building, Floor, Etc."
          field="physicalAptSuiteBuildingFloorNumber"
          validationSchema={
            dataEntryMode === 'new'
              ? LEAManagementValidationSchema
              : LEAManagementValidationEditSchema
          }
          disabled={!isStateAdmin()}
        />
      </div>
      <div className="field-row">
        <FormTextbox
          displayName="Physical City"
          field="physicalCity"
          classConfig={{ labelClasses: getRequiredPhysicalAddressLabel() }}
          validationSchema={
            dataEntryMode === 'new'
              ? LEAManagementValidationSchema
              : LEAManagementValidationEditSchema
          }
          onChangeEvent={updatePhysicalAddressRequiredFlag}
          disabled={!isStateAdmin()}
        />
        <FormDropdown
          displayName="State"
          field="physicalState"
          options={formViewModel.stateOptions}
          classConfig={{ labelClasses: getRequiredPhysicalAddressLabel() }}
          validationSchema={
            dataEntryMode === 'new'
              ? LEAManagementValidationSchema
              : LEAManagementValidationEditSchema
          }
          onChangeEvent={updateShowPhysicalCounty}
          disabled={!isStateAdmin()}
          isClearable
        />
        <FormTextbox
          displayName="Zip Code"
          field="physicalZipCode"
          classConfig={{ labelClasses: getRequiredPhysicalAddressLabel() }}
          validationSchema={
            dataEntryMode === 'new'
              ? LEAManagementValidationSchema
              : LEAManagementValidationEditSchema
          }
          onChangeEvent={updatePhysicalAddressRequiredFlag}
          disabled={!isStateAdmin()}
        />
      </div>
      {isShowPhysicalCounty && (
        <div className="field-row">
          <FormDropdown
            displayName="County"
            field="physicalCounty"
            options={formViewModel.countyOptions}
            classConfig={{
              labelClasses: getRequiredPhysicalAddressLabel(),
            }}
            validationSchema={
              dataEntryMode === 'new'
                ? LEAManagementValidationSchema
                : LEAManagementValidationEditSchema
            }
            onChangeEvent={updatePhysicalCountyAnsiCode}
            disabled={!isStateAdmin()}
            isClearable
          />
          <FormTextbox
            displayName="County ANSI Code"
            field="physicalCountyAnsiCode"
            disabled={true}
          />
        </div>
      )}
    </Panel>
  );
};

export default LEAManagementPhysicalAddressPanel;
